import { useState } from 'react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { PRODUCT_PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { studentProjectsInfiniteQueryOptions } from '../api/productRequisition.api';
import { getStudentProjectColumns } from '../components/RequisitionColumns';
import { StudentProjectFormModal } from '../components/StudentProjectFormModal';
import { useRequisitionFilters } from '../hooks/useRequisitionFilters';
import type { StudentProject, StudentProjectSearch } from '../types/productRequisition.types';

const route = getRouteApi('/_auth/product/student-collection-projects/');

const NUMERIC_KEYS: readonly string[] = [];

export function StudentProjectListPage() {
  const navigate = useNavigate();
  const routeSearch = route.useSearch();
  const routeNavigate = route.useNavigate();
  const controller = useRequisitionFilters({
    search: routeSearch,
    numericKeys: NUMERIC_KEYS,
    onApplyFilters: (next) =>
      void routeNavigate({ search: (previous) => ({ ...previous, ...next }), replace: true }),
    onApplySearch: (text) =>
      void routeNavigate({ search: (previous) => ({ ...previous, search: text }), replace: true }),
  });
  const params = controller.params as StudentProjectSearch;

  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(studentProjectsInfiniteQueryOptions(params));

  const [editing, setEditing] = useState<StudentProject | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const { can } = usePermissions();

  const columns = getStudentProjectColumns(params.search, {
    ...(can(PRODUCT_PERMISSIONS.STUDENT_PROJECT_EDIT)
      ? {
          onEdit: (row: StudentProject) => {
            setEditing(row);
            setFormOpen(true);
          },
        }
      : {}),
    onEnter: (row) =>
      void navigate({
        to: '/product/student-collection-projects/$projectId',
        params: { projectId: row.id },
      }),
  });

  const stats = [
    { key: 'projects', label: 'Projects', value: firstPageMeta?.total },
    { key: 'entries', label: 'Entries', value: firstPageMeta?.totals.entries },
    { key: 'students', label: 'Students', value: firstPageMeta?.totals.students },
  ];

  return (
    <CatalogueListLayout
      title="Mkt Student Collection Project"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search projects',
      }}
      stats={{
        cards: stats,
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      onAdd={() => {
        setEditing(null);
        setFormOpen(true);
      }}
      addLabel="Add Student Project"
      addRequires={PRODUCT_PERMISSIONS.STUDENT_PROJECT_CREATE}
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the projects"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No project matches"
          {...loadMoreProps}
        />
      )}

      <StudentProjectFormModal project={editing} open={isFormOpen} onOpenChange={setFormOpen} />
    </CatalogueListLayout>
  );
}
